import React, { useState } from 'react';
import { X } from 'lucide-react';

const TermsPrivacyModal = ({ isOpen, onClose, type }) => {
  const [activeTab, setActiveTab] = useState(type || 'terms');

  if (!isOpen) return null;

  const termsContent = (
    <div className="prose prose-sm max-w-none">
      <section className="mb-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-3">1. Acceptance of Terms</h2>
        <p className="text-gray-700 mb-3">
          By accessing and using MyDigiSign ("the Service"), you accept and agree to be bound by the terms and provision of this agreement. If you do not agree to abide by the above, please do not use this service.
        </p>
      </section>

      <section className="mb-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-3">2. Description of Service</h2>
        <p className="text-gray-700 mb-3">
          MyDigiSign is a digital signage management platform that allows users to create, manage, and display digital content on connected devices. The service includes:
        </p>
        <ul className="list-disc pl-6 text-gray-700 mb-3">
          <li>Content creation and management tools</li>
          <li>Device management and monitoring</li>
          <li>Schedule management for content display</li>
          <li>Analytics and reporting features</li>
          <li>Channel management capabilities</li>
          <li>AI-powered content generation tools</li>
        </ul>
      </section>

      <section className="mb-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-3">3. User Accounts and Registration</h2>
        <p className="text-gray-700 mb-3">
          To access certain features of the Service, you must register for an account. You agree to:
        </p>
        <ul className="list-disc pl-6 text-gray-700 mb-3">
          <li>Provide accurate, current, and complete information during registration</li>
          <li>Maintain and update your account information to keep it accurate and current</li>
          <li>Maintain the security of your account credentials</li>
          <li>Accept responsibility for all activities that occur under your account</li>
          <li>Notify us immediately of any unauthorized use of your account</li>
        </ul>
      </section>

      <section className="mb-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-3">4. Acceptable Use Policy</h2>
        <p className="text-gray-700 mb-3">
          You agree not to use the Service to:
        </p>
        <ul className="list-disc pl-6 text-gray-700 mb-3">
          <li>Upload, display, or transmit content that is illegal, harmful, threatening, abusive, or defamatory</li>
          <li>Violate any applicable laws or regulations</li>
          <li>Infringe upon the rights of others, including intellectual property rights</li>
          <li>Attempt to gain unauthorized access to the Service or other users' accounts</li>
          <li>Interfere with or disrupt the Service or servers</li>
          <li>Use the Service for commercial purposes without proper authorization</li>
          <li>Display content that contains malware, viruses, or other harmful code</li>
        </ul>
      </section>

      <section className="mb-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-3">5. Content Ownership and Licensing</h2>
        <p className="text-gray-700 mb-3">
          You retain ownership of content you create and upload to the Service. However, you grant us a limited license to:
        </p>
        <ul className="list-disc pl-6 text-gray-700 mb-3">
          <li>Store and process your content to provide the Service</li>
          <li>Display your content on your authorized devices</li>
          <li>Use your content for service improvement and analytics (anonymized where possible)</li>
        </ul>
        <p className="text-gray-700 mb-3">
          You are responsible for ensuring you have the rights to use any content you upload, including images, videos, and text.
        </p>
      </section>
    </div>
  );

  const privacyContent = (
    <div className="prose prose-sm max-w-none">
      <section className="mb-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-3">1. Introduction</h2>
        <p className="text-gray-700 mb-3">
          MyDigiSign ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our digital signage management platform and related services.
        </p>
        <p className="text-gray-700 mb-3">
          By using our Service, you consent to the data practices described in this policy. If you do not agree with our policies and practices, please do not use our Service.
        </p>
      </section>

      <section className="mb-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-3">2. Information We Collect</h2>
        
        <h3 className="text-lg font-semibold text-gray-800 mb-2">2.1 Personal Information</h3>
        <p className="text-gray-700 mb-3">
          We may collect personal information that you provide directly to us, including:
        </p>
        <ul className="list-disc pl-6 text-gray-700 mb-3">
          <li>Name and contact information (email address, phone number)</li>
          <li>Account credentials and profile information</li>
          <li>Company or organization details</li>
          <li>Communication preferences</li>
        </ul>

        <h3 className="text-lg font-semibold text-gray-800 mb-2">2.2 Usage Information</h3>
        <p className="text-gray-700 mb-3">
          We automatically collect certain information about your use of the Service, including:
        </p>
        <ul className="list-disc pl-6 text-gray-700 mb-3">
          <li>Device information (IP address, browser type, operating system)</li>
          <li>Usage patterns and analytics data</li>
          <li>Content creation and management activities</li>
          <li>Device connection and performance metrics</li>
          <li>Error logs and system diagnostics</li>
        </ul>

        <h3 className="text-lg font-semibold text-gray-800 mb-2">2.3 Content and Media</h3>
        <p className="text-gray-700 mb-3">
          We store and process content you upload to the Service, including:
        </p>
        <ul className="list-disc pl-6 text-gray-700 mb-3">
          <li>Images, videos, and multimedia files</li>
          <li>Schedule and playlist configurations</li>
          <li>Device settings and configurations</li>
        </ul>
      </section>

      <section className="mb-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-3">3. How We Use Your Information</h2>
        <p className="text-gray-700 mb-3">
          We use the information we collect for various purposes, including:
        </p>
        <ul className="list-disc pl-6 text-gray-700 mb-3">
          <li>Providing and maintaining the Service</li>
          <li>Personalizing your experience and improving our services</li>
          <li>Communicating with you about updates, security, and support</li>
          <li>Analyzing usage patterns to enhance functionality</li>
          <li>Ensuring compliance with legal obligations</li>
          <li>Preventing fraud and maintaining security</li>
        </ul>
      </section>

      <section className="mb-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-3">4. Information Sharing and Disclosure</h2>
        <p className="text-gray-700 mb-3">
          We do not sell, trade, or otherwise transfer your personal information to third parties without your consent, except as described in this policy. We may share your information in the following circumstances:
        </p>
        <ul className="list-disc pl-6 text-gray-700 mb-3">
          <li>With your explicit consent</li>
          <li>To comply with legal obligations</li>
          <li>To protect our rights and safety</li>
          <li>With service providers who assist in operating our Service</li>
        </ul>
      </section>
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[85vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 flex-shrink-0">
          <div className="flex space-x-4">
            <button
              onClick={() => setActiveTab('terms')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                activeTab === 'terms'
                  ? 'bg-primary-100 text-primary-700'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Terms of Service
            </button>
            <button
              onClick={() => setActiveTab('privacy')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                activeTab === 'privacy'
                  ? 'bg-primary-100 text-primary-700'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Privacy Policy
            </button>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto flex-1">
          {activeTab === 'terms' ? termsContent : privacyContent}
        </div>

        {/* Footer */}
        <div className="flex justify-end p-6 border-t border-gray-200 flex-shrink-0">
          <button
            onClick={onClose}
            className="px-8 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium text-base shadow-lg hover:shadow-xl"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default TermsPrivacyModal;
